import debounce from "lodash-es/debounce"
import type { CookieRef } from "nuxt/app"

export const duoppBaseUrl = "https://mapserver.viewer-duopp.nl/?map=/etc/mapserver" as const
export const pdokBaseUrl = "https://service.pdok.nl/rioned/beheerstedelijkwater/wms/v1_0" as const

export interface DashboardMapView {
  /** Prevents restoring the initial values before fitBounds (or similar) is called */
  initialized: boolean
  coordinates: { lng: number; lat: number }
  zoom: number
  selectedFeatures?: Array<string>
}

export interface SamMapLayer {
  enabled: CookieRef<boolean>
  layer: Ref<string>
  mapIndex: Ref<number>
  legendUrl: Ref<string>
}

export interface MapFeatureData {
  layerKey: string
  features: Array<{ properties: Record<string, unknown> }>
}

export const geoPointToLatLng = (geoPoint: GeoPoint): LatLngAlt =>
  geoPoint.coordinates[2]
    ? {
        lng: geoPoint.coordinates[0],
        lat: geoPoint.coordinates[1],
        alt: geoPoint.coordinates[2],
      }
    : {
        lng: geoPoint.coordinates[0],
        lat: geoPoint.coordinates[1],
      }

export const latLngToGeoPoint = (latLngAlt: LatLngAlt): GeoPoint => ({
  type: "Point",
  coordinates: latLngAlt.alt
    ? // prettier-ignore
      [
      parseFloat(latLngAlt.lng.toFixed(7)),
      parseFloat(latLngAlt.lat.toFixed(7)),
      latLngAlt.alt]
    : [parseFloat(latLngAlt.lng.toFixed(7)), parseFloat(latLngAlt.lat.toFixed(7))],
})

export const assetRelationToPath = (relation: PopulatedAssetRelation) => [
  relation.asset1.location?.gps ? geoPointToLatLng(relation.asset1.location?.gps) : defaultCoords,
  relation.asset2.location?.gps ? geoPointToLatLng(relation.asset2.location?.gps) : defaultCoords,
]

export const pdokLayers = [
  "BeheerGebied",
  "BeheerPut",
  "BeheerLeiding",
  "BeheerPomp",
  "BeheerLozing",
  "BeheerBouwwerk",
  "AansluitingLeiding",
  "AansluitingPunt",
]

export interface IconSettings {
  color?: string
  label?: string
  highlighted?: boolean
  showBadge?: boolean
  size?: MarkerSize
}

const markerSizeDimensions = {
  [MarkerSize.SMALL]: { width: 30, height: 34 },
  [MarkerSize.MEDIUM]: { width: 36, height: 40 },
  [MarkerSize.LARGE]: { width: 44, height: 48 },
}

export const svgAssetUrl = (label: string, color = "grey", strokeWidth = 1, strokeColor = "#fff") =>
  `/markers/asset?label=${encodeURIComponent(label)}${color ? "&color=" + encodeURIComponent(color) : ""}&strokeWidth=${strokeWidth}&strokeColor=${encodeURIComponent(strokeColor)}`

export const svgTicketUrl = (type: TicketType, strokeWidth = 1, strokeColor = "#fff") =>
  `/markers/ticket?type=${type}&strokeWidth=${strokeWidth}&strokeColor=${encodeURIComponent(strokeColor)}`

export const generateMarkerIcon = (options: IconSettings) => {
  const { color, label, highlighted, showBadge, size } = options

  const strokeWidth = highlighted ? 1.5 : 1
  const strokeColor = highlighted ? "%23496cae" : "%23ffffff"

  const generateBadge = (color?: string) =>
    `<circle cx="14" cy="1" r="2.5" stroke="${strokeColor}" stroke-width="${highlighted ? 1 : 0.7}" fill="${
      color ? encodeURIComponent(color) : "grey"
    }" />`

  const sizeDimensions = markerSizeDimensions[size ?? MarkerSize.MEDIUM]

  return {
    url: `data:image/svg+xml;utf-8, \
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="20" viewBox="-1 -1 18 20"> \
        <style> text { fill: white; font: 400 7px Outfit, Arial, sans-serif; } </style> \
        <path d="m 3 0 l 10 0 c 2 0 3 1 3 3 l 0 10 c 0 2 -1 3 -3 3 l -1 0 l -4 2 l -4 -2 l -1 0 c -2 0 -3 -1 -3 -3 l 0 -10 c 0 -2 1 -3 3 -3 Z" stroke="${strokeColor}" stroke-width="${strokeWidth}" fill="${
          color ? encodeURIComponent(color) : "grey"
        }"/> \
        <text x="46%" y="46%" dominant-baseline="middle" text-anchor="middle" fill="white" font-size="12px">${label ?? "?"}</text> \
        ${showBadge ? generateBadge("#C10015") : ""} \
      </svg>`,
    scaledSize: sizeDimensions,
    anchor: { x: sizeDimensions.width / 2, y: sizeDimensions.height },
  }
}

export const navigateToDestination = (destination: { lat: number; lng: number }, currentDestination?: { lat: number; lng: number }) => {
  const isApple = /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform)

  if (isApple) {
    return `maps://maps.apple.com/?daddr=${destination.lat},${destination.lng}${
      currentDestination ? `&saddr=${currentDestination.lat},${currentDestination.lng}` : ""
    }`
  }

  return `https://www.google.com/maps/dir/?api=1&travelmode=driving&layer=traffic&destination=${destination.lat},${destination.lng}${
    currentDestination ? `&origin=${currentDestination.lat},${currentDestination.lng}` : ""
  }`
}

export const generateCenter = (coordinates: Array<[lng: number, lat: number]>) => {
  if (!coordinates.length) {
    return defaultCoords
  }

  const [lng, lat] = coordinates.reduce((acc, curr) => [acc[0] + curr[0], acc[1] + curr[1]])
  return {
    lng: lng / coordinates.length,
    lat: lat / coordinates.length,
  }
}

export const generateLayerLegendUrl = (layerKey: string, duoPPKey?: string, layerName?: string) => {
  if (duoPPKey) {
    const query = new URLSearchParams({
      SERVICE: "WMS",
      VERSION: "1.3.0",
      REQUEST: "GetLegendGraphic",
      FORMAT: "image/png",
      SLD_VERSION: "1.1.0",
      LAYER: layerKey,
    })

    return `${duoppBaseUrl}/${duoPPKey}&${query}`
  }

  return `${pdokBaseUrl}/legend/${layerKey}/GWSW_${layerName}.png`
}

export const generateDuoPPLayerCapabilitiesUrl = (layerKey: string): string => {
  const query = new URLSearchParams({
    SERVICE: "WMS",
    VERSION: "1.3.0",
    REQUEST: "GetCapabilities",
  })
  return `https://mapserver.viewer-duopp.nl/?map=/etc/mapserver/${layerKey}&${query}`
}

export const generateGISLayerCapabilitiesUrl = (url: string): string => {
  const query = new URLSearchParams({
    SERVICE: "WFS",
    VERSION: "2.0.0",
    REQUEST: "GetCapabilities",
  })
  return `${url}?${query}`
}

export const generateLayerFeaturesUrl = (scaleFactor: number, pointOfInterest: LatLngAlt, layerKey: string): string => {
  const clickArea = 600
  const magicNumber = 50
  const magicSizeNumber = 101

  const xs = (pointOfInterest.lat * scaleFactor - clickArea / 2) / scaleFactor
  const xm = (pointOfInterest.lat * scaleFactor + clickArea / 2) / scaleFactor
  const ys = (pointOfInterest.lng * scaleFactor - clickArea / 2) / scaleFactor
  const ym = (pointOfInterest.lng * scaleFactor + clickArea / 2) / scaleFactor

  // Only allow for one layer per call. WMS returns json for one layer, and XML for the other if you do multiple at a time.
  const query = new URLSearchParams({
    SERVICE: "WMS",
    VERSION: "1.3.0",
    REQUEST: "GetFeatureInfo",
    QUERY_LAYERS: layerKey,
    LAYERS: layerKey,
    INFO_FORMAT: "application/json",
    I: magicNumber.toString(),
    J: magicNumber.toString(),
    WIDTH: magicSizeNumber.toString(),
    HEIGHT: magicSizeNumber.toString(),
    CRS: "EPSG:4326",
    BBOX: `${xs},${ys},${xm},${ym}`,
  })

  return `${pdokBaseUrl}?${query}`
}

export const getLayerFeatures = async (
  scaleFactor: number,
  pointOfInterest: LatLngAlt,
  layerKeys: Array<string>,
): Promise<Array<MapFeatureData>> => {
  const generatedUrls = layerKeys.map((key) => {
    return {
      url: generateLayerFeaturesUrl(scaleFactor, pointOfInterest, key),
      layerKey: key,
    }
  })
  const data = await Promise.all(
    generatedUrls.map(async (generatedUrl) => {
      try {
        const response = await fetch(generatedUrl.url)
        const body: MapFeatureData = { layerKey: generatedUrl.layerKey, ...(await response.json()) }

        if (!body.features.length) {
          return null
        }

        return body
      } catch {
        return null
      }
    }),
  )

  return data.filter((feature): feature is MapFeatureData => !!feature)
}

export const useMapLayerForGoogleMaps = async (
  map: Ref<google.maps.Map | undefined>,
  mapIndex: number,
  layer: string,
  duoPPKey?: string,
): Promise<SamMapLayer> => {
  const { $googleApiPromise } = useNuxtApp()
  const loadedGoogle = (await $googleApiPromise) as typeof google

  const { generateLayerOptions } = await useGoogleMapHelpers()

  const enabled = useLocalStorage(`map-layer-${layer}${duoPPKey ? `-${duoPPKey}` : ""}`, false)

  const legendUrl = ref(generateLayerLegendUrl(layer, duoPPKey))

  const updateLayer = debounce(() => {
    if (enabled.value) {
      const overlayOptions = generateLayerOptions(map.value?.getProjection(), layer, duoPPKey)
      const overlay = new loadedGoogle.maps.ImageMapType(overlayOptions)
      map.value?.overlayMapTypes.setAt(mapIndex, overlay)
    } else {
      map.value?.overlayMapTypes.setAt(mapIndex, null)
    }
  }, 400)

  watch(enabled, updateLayer, { immediate: true })

  return {
    enabled,
    mapIndex: ref(mapIndex),
    layer: ref(layer),
    legendUrl: legendUrl,
  }
}

export const defaultCoords: LatLngAlt = { lat: 52.100916, lng: 5.646269 }
